import React, { useEffect, useId, useRef, useState } from 'react';
import { Field, FieldHint, Input, Label } from '../form-kit';
import { AppButton } from '../button/app-button';
import { parseQuickAdd } from '../../utils/pass-quick-add';
import { parseAvailability, parsePass } from '../../api/tenant-passes-client';
import { readPassError } from '../../utils/pass-errors';
import type { PassSpecInput } from '../../interfaces/visitor-pass';
import type { QuickAddApi } from './pass-editor';
import {
  IqFillButton,
  IqFillRow,
  QuickAddInputWrap,
  QuickAddLine,
  QuickAddRow,
  QuickAddStatus,
} from './pass-editor.styled';

/**
 * QUICK-ADD — one sentence in, the form filled in (resident-portal RP-4.5).
 *
 * "My cleaner Maria, Tuesdays 9–1, until 31 Dec" goes through the local parser
 * (utils/pass-quick-add, no network, no AI) and its fields are handed to the
 * editor, which fills them the way typing would. It NEVER saves: the reader
 * checks the form and presses Save themselves.
 *
 * The input sits inside the editor's <form>, so Enter is taken here — it fills
 * the form instead of submitting a half-read pass.
 *
 * What was understood, and what was not, is said in ONE polite status region
 * under the field, in plain words: "Filled in: name, who, days." and "Not
 * understood: …". The second line is there WHENEVER words were left over —
 * also when nothing at all could be filled in — and "name" is listed only when
 * a name really was set (owner decision RP-D21: a field the sentence does not
 * support stays empty, and the reader is told which words were not read).
 *
 * LOBBERO IQ, AS A SECOND READ (resident-portal Plan 5, RP-5.8). When the local
 * parser left something it did not understand — or understood nothing — and the
 * server says the fallback is available (the organisation has Lobbero IQ and it
 * is switched on), "Fill with Lobbero IQ" asks the model for the days, times,
 * dates and who. The model never sees a name: the server rebuilds the sentence
 * from schedule words only. Its answer fills ONLY what the local parser left
 * empty, the same way — it never saves, and it never overrides what the reader
 * already sees filled in.
 */

/* The form keys the editor reports, as the words a reader would use. In form
   order, so the sentence reads top to bottom like the form. */
const FILLED_WORDS: ReadonlyArray<[ReadonlyArray<string>, string]> = [
  [['first_name', 'last_name'], 'name'],
  [['company'], 'company'],
  [['category'], 'who'],
  [['valid_from'], 'date'],
  [['days'], 'days'],
  [['interval_weeks'], 'how often'],
  [['any_time', 'window_start', 'window_end'], 'times'],
  [['valid_until'], 'end date'],
  [['max_party'], 'party size'],
  [['note'], 'note'],
];

export const filledWords = (keys: ReadonlyArray<string>): string[] =>
  FILLED_WORDS.filter(([group]) => group.some(key => keys.includes(key))).map(([, word]) => word);

export const QUICK_ADD_COPY = {
  label: 'Describe the visit',
  hint: 'e.g. My cleaner Maria, Tuesdays 9–1, until 31 Dec',
  button: 'Fill in the form',
  nothing: "I couldn't fill anything in from that. Fill in the form below.",
  filled: (words: string[]) => `Filled in: ${words.join(', ')}.`,
  leftovers: (parts: string[]) => `Not understood: ${parts.join(', ')}`,
  iqButton: 'Fill with Lobbero IQ',
  iqBusy: 'Reading…',
  iqNote: 'Lobbero IQ reads the days and times only, never names.',
  iqFilled: (words: string[]) => `Lobbero IQ filled in: ${words.join(', ')}.`,
  iqNothing: "Lobbero IQ couldn't fill in anything more. Fill in the rest below.",
  // The IQ read was refused or never answered: said in its own words, never as
  // "nothing more to fill in" (RP-9.6).
  iqFailed: 'Lobbero IQ could not read that just now. Fill in the rest below.',
} as const;

interface Outcome {
  filled: string[];
  leftovers: string[];
  /** The local parse, kept for the IQ fallback: its name words and its keys. */
  names: string[];
  localKeys: string[];
  sentence: string;
  /** What the IQ fallback added (null = not asked yet). */
  iq: string[] | null;
  /** Why the IQ read did not happen, when it did not. */
  iqProblem?: string;
}

export interface QuickAddFieldProps {
  api: QuickAddApi;
  /** The building's calendar date — "Tuesdays" and "until 31 Dec" count from it. */
  today: string;
  /** The building's zone. */
  zone?: string;
}

export const QuickAddField: React.FC<QuickAddFieldProps> = ({ api, today, zone }) => {
  const rawId = useId();
  const base = `quick-add-${rawId.replace(/[^a-zA-Z0-9_-]/g, '')}`;
  const inputId = `${base}-input`;
  const hintId = `${base}-hint`;
  const [text, setText] = useState('');
  const [outcome, setOutcome] = useState<Outcome | null>(null);
  const [iqAvailable, setIqAvailable] = useState(false);
  const [iqBusy, setIqBusy] = useState(false);
  const alive = useRef(true);

  useEffect(() => {
    alive.current = true;
    // Wrapped, so a loader that returns nothing (a mocked client) is "no".
    Promise.resolve()
      .then(() => parseAvailability())
      .then(ok => {
        if (alive.current) setIqAvailable(ok === true);
      })
      .catch(() => undefined);

    return () => {
      alive.current = false;
    };
  }, []);

  const fill = () => {
    if (api.disabled) return;
    const sentence = text.trim();
    if (!sentence) return;

    let fields: Partial<PassSpecInput> = {};
    let leftovers: string[] = [];
    try {
      const result = parseQuickAdd(sentence, today, zone);
      fields = result.fields || {};
      leftovers = (result.leftovers || [])
        .map((part: string) => part.trim())
        .filter(Boolean);
    } catch {
      // A sentence the parser chokes on is a sentence it did not understand.
      fields = {};
    }

    const keys = api.apply(fields);
    const names = [fields.first_name, fields.last_name].filter(
      (n): n is string => typeof n === 'string' && !!n.trim()
    );
    setOutcome({ filled: filledWords(keys), leftovers, names, localKeys: keys, sentence, iq: null });
  };

  const askIq = async () => {
    if (!outcome || iqBusy || api.disabled) return;
    setIqBusy(true);
    let added: string[] = [];
    let problem = '';
    try {
      // The home the pass is for travels with the sentence when the form knows
      // it, so "Tuesday" is read on that building's clock.
      const fields = api.hostOccupancyId
        ? await parsePass(outcome.sentence, outcome.names, api.hostOccupancyId)
        : await parsePass(outcome.sentence, outcome.names);
      // Only what the local parser left empty: the reader has already seen the
      // rest filled in, and a second opinion must not quietly overwrite it.
      const fresh = Object.fromEntries(
        Object.entries(fields).filter(([key]) => {
          if (key === 'window_start' || key === 'window_end') {
            return !outcome.localKeys.some(k => k === 'window_start' || k === 'window_end' || k === 'any_time');
          }

          return !outcome.localKeys.includes(key);
        })
      ) as Partial<PassSpecInput>;
      added = Object.keys(fresh).length ? api.apply(fresh) : [];
    } catch (error) {
      added = [];
      // A rate limit, a lost connection and a refusal each say what they are.
      problem = readPassError(error, { fallback: QUICK_ADD_COPY.iqFailed }).message;
    }
    if (!alive.current) return;
    setIqBusy(false);
    setOutcome(current =>
      current ? { ...current, iq: filledWords(added), iqProblem: problem } : current
    );
  };

  const offerIq =
    iqAvailable && !!outcome && outcome.iq === null && (outcome.leftovers.length > 0 || outcome.filled.length === 0);

  return (
    <Field>
      <Label htmlFor={inputId}>{QUICK_ADD_COPY.label}</Label>
      <QuickAddRow>
        <QuickAddInputWrap>
          <Input
            id={inputId}
            type="text"
            value={text}
            autoComplete="off"
            enterKeyHint="done"
            aria-describedby={hintId}
            disabled={api.disabled}
            onChange={event => setText(event.target.value)}
            onKeyDown={event => {
              if (event.key !== 'Enter' || event.nativeEvent.isComposing) return;
              event.preventDefault();
              fill();
            }}
          />
        </QuickAddInputWrap>
        <AppButton variant="secondary" disabled={api.disabled} onClick={fill}>
          {QUICK_ADD_COPY.button}
        </AppButton>
      </QuickAddRow>
      <FieldHint id={hintId} aria-live="off">
        {QUICK_ADD_COPY.hint}
      </FieldHint>
      <QuickAddStatus role="status" aria-live="polite" aria-atomic="true">
        {outcome ? (
          <>
            <QuickAddLine>
              {outcome.filled.length ? QUICK_ADD_COPY.filled(outcome.filled) : QUICK_ADD_COPY.nothing}
            </QuickAddLine>
            {outcome.leftovers.length ? (
              <QuickAddLine>{QUICK_ADD_COPY.leftovers(outcome.leftovers)}</QuickAddLine>
            ) : null}
          </>
        ) : null}
        {outcome && outcome.iq !== null ? (
          <QuickAddLine>
            {outcome.iq.length
              ? QUICK_ADD_COPY.iqFilled(outcome.iq)
              : outcome.iqProblem || QUICK_ADD_COPY.iqNothing}
          </QuickAddLine>
        ) : null}
      </QuickAddStatus>
      {offerIq ? (
        <IqFillRow>
          <IqFillButton
            type="button"
            onClick={() => void askIq()}
            disabled={iqBusy || api.disabled}
            aria-busy={iqBusy || undefined}
          >
            {iqBusy ? QUICK_ADD_COPY.iqBusy : QUICK_ADD_COPY.iqButton}
          </IqFillButton>
          <FieldHint>{QUICK_ADD_COPY.iqNote}</FieldHint>
        </IqFillRow>
      ) : null}
    </Field>
  );
};
